/**
 * Analysis page — 3D viewer with orientation cards on the left,
 * DFM report + Bob's advice + chat on the right.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DesignCheckLayout from './DesignCheckLayout';
import Viewer3D from '../../components/design-check/Viewer3D';
import ChecksPanel from '../../components/design-check/ChecksPanel';
import AdvisorChat from '../../components/design-check/AdvisorChat';
import { ChatMessage } from '../../components/chatbot';
import { parseSTEP, decimateMesh } from '../tools/cte-utils';
import './DesignCheck.css';

const VIEWER_MAX_TRIS = 300000;

function AnalysisPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [fileName, setFileName] = useState('');
  const [positions, setPositions] = useState(null);
  const [selectedOrientation, setSelectedOrientation] = useState(null);
  const [overlay, setOverlay] = useState('overhangs'); // overhangs | issues | none
  const [tab, setTab] = useState('report');
  const [advice, setAdvice] = useState(null);
  const [guideMap, setGuideMap] = useState({});
  const pollRef = useRef(null);

  // Poll job status until done, then load the report. Give up after 10
  // minutes so a hung job shows an honest error instead of spinning forever.
  useEffect(() => {
    let stopped = false;
    const deadline = Date.now() + 10 * 60 * 1000;
    const poll = async () => {
      if (Date.now() > deadline) {
        setError('The analysis is taking much longer than expected. Go back and check the status of your analysis — if it still has not finished, delete it and upload the part again.');
        setStatus('error');
        return;
      }
      try {
        const res = await fetch(`/api/design-check/jobs/${jobId}/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.status === 401 || res.status === 403) { navigate('/design-check'); return; }
        const data = await res.json();
        if (stopped) return;
        if (data.status === 'done') {
          const rep = await fetch(`/api/design-check/jobs/${jobId}/report`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then((r) => r.json());
          if (rep.success) {
            setReport(rep.report);
            setFileName(rep.job.fileName || rep.report.part.fileName);
            setSelectedOrientation(rep.report.recommended.orientationId);
            setStatus('done');
          } else {
            setError(rep.error || 'Report could not be loaded');
            setStatus('error');
          }
        } else if (data.status === 'error') {
          setError(data.error || 'Analysis failed');
          setStatus('error');
        } else {
          setStatus(data.status || 'processing');
          pollRef.current = setTimeout(poll, 2500);
        }
      } catch (err) {
        if (!stopped) pollRef.current = setTimeout(poll, 4000);
      }
    };
    poll();
    return () => { stopped = true; clearTimeout(pollRef.current); };
  }, [jobId, token, navigate]);

  // Fetch + parse the STEP for the viewer once the analysis exists
  useEffect(() => {
    if (status !== 'done' || positions) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/design-check/jobs/${jobId}/file`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const buffer = await res.arrayBuffer();
        const soup = await parseSTEP(buffer);
        const { positions: decimated } = decimateMesh(soup, VIEWER_MAX_TRIS);
        if (!cancelled) setPositions(decimated);
      } catch (err) {
        console.error('Viewer mesh load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [status, jobId, token, positions]);

  // Bob's narrative advice — generated asynchronously after analysis.
  // Poll for ~1 minute; if it still isn't there (e.g. AI momentarily down),
  // stop and offer a manual retry instead of spinning forever.
  const [adviceStalled, setAdviceStalled] = useState(false);
  useEffect(() => {
    if (status !== 'done' || advice) return;
    let cancelled = false;
    let attempts = 0;
    let timer;
    const fetchAdvice = async () => {
      try {
        const res = await fetch(`/api/design-check/jobs/${jobId}/advice`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (cancelled) return;
        if (data.success && data.ready) { setAdvice(data.markdown); return; }
      } catch (err) { /* fall through to retry */ }
      if (cancelled) return;
      attempts += 1;
      if (attempts >= 15) setAdviceStalled(true);
      else timer = setTimeout(fetchAdvice, 4000);
    };
    fetchAdvice();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [status, jobId, token, advice]);

  const exportPdf = async () => {
    const { generateDesignCheckPDF } = await import('./design-check-report-pdf');
    generateDesignCheckPDF({ report, fileName, advice });
  };

  const deleteAnalysis = async () => {
    if (!window.confirm(`Delete "${fileName}" and its report? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/design-check/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Delete failed');
      navigate('/design-check');
    } catch (err) {
      window.alert(err.message);
    }
  };

  const [adviceError, setAdviceError] = useState('');
  const retryAdvice = async () => {
    setAdviceStalled(false);
    setAdviceError('');
    try {
      const res = await fetch(`/api/design-check/jobs/${jobId}/advice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.ready) {
        setAdvice(data.markdown);
      } else {
        // e.g. the invite's regeneration limit — show the real reason
        if (data.error) setAdviceError(data.error);
        setAdviceStalled(true);
      }
    } catch (err) {
      setAdviceStalled(true);
    }
  };

  // Guide sections → per-check deep links
  useEffect(() => {
    fetch('/api/design-check/guide', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) return;
        const map = {};
        for (const s of d.sections) {
          for (const checkId of s.checkIds || []) {
            if (!map[checkId]) map[checkId] = s;
          }
        }
        setGuideMap(map);
      })
      .catch(() => {});
  }, [token]);

  const orientation = useMemo(
    () => report?.orientations.find((o) => o.id === selectedOrientation) || report?.orientations[0],
    [report, selectedOrientation]
  );

  const markers = useMemo(() => {
    if (!report || !orientation) return [];
    const out = [];
    for (const span of orientation.checks.bridging.spans || []) {
      out.push({ kind: 'bridge', p: span.centroid });
    }
    for (const h of (report.wallThickness.hotspots || []).slice(0, 6)) {
      if (report.wallThickness.status !== 'pass') out.push({ kind: 'wall', p: h.p });
    }
    return out;
  }, [report, orientation]);

  if (status !== 'done') {
    return (
      <DesignCheckLayout>
        <div className="dc-progress-page">
          {status === 'error' ? (
            <>
              <h2>Analysis failed</h2>
              <p style={{ color: 'var(--rf-text-secondary)' }}>{error}</p>
              <button className="dc-btn ghost" onClick={() => navigate('/design-check')}>← Back</button>
            </>
          ) : (
            <>
              <div className="dc-spinner" />
              <h2>{status === 'queued' ? 'Waiting in the queue…' : 'Analysing your part…'}</h2>
              <p style={{ color: 'var(--rf-text-secondary)' }}>
                Meshing the geometry, testing every stable build orientation,
                measuring walls, overhangs and bridges. Large parts can take a minute.
              </p>
            </>
          )}
        </div>
      </DesignCheckLayout>
    );
  }

  return (
    <DesignCheckLayout>
      <div className="dc-page-actions">
        <button className="dc-btn ghost" onClick={exportPdf}>
          Export PDF{!advice ? ' (advice not ready yet)' : ''}
        </button>
        <button className="dc-btn ghost danger" onClick={deleteAnalysis}>Delete</button>
      </div>
      {report.part.truncated && (
        <div className="dc-warning-banner">
          This part's mesh was larger than the analysis limit, so it was simplified before
          checking — results may miss detail in some areas. For a full check, export the STEP
          with a coarser tessellation or split the assembly into separate parts.
        </div>
      )}
      {report.part.surfaceModel && (
        <div className="dc-warning-banner">
          Analysed as a surface (mid-surface) model — the CAD has no wall thickness, so the
          print is a single {report.recommended.beadWidthMm}mm bead laid centred on the surface,
          the same way Ai Build slices mid-surface geometry. Weight, time and wall thickness all
          come from that bead, not from an enclosed volume.
        </div>
      )}
      <div className="dc-analysis">
        <div>
          <div className="dc-card dc-viewer-card">
            {positions ? (
              <Viewer3D
                positions={positions}
                orientation={orientation}
                thresholds={report.machine.thresholds}
                envelopeMm={report.machine.envelopeMm}
                markers={markers}
                overlay={overlay}
              />
            ) : (
              <div className="dc-viewer-canvas" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="dc-spinner" />
              </div>
            )}
            <div className="dc-viewer-hud">
              <span className="dc-file-tag">{fileName}</span>
              <div className="dc-overlay-toggle">
                <button className={overlay === 'overhangs' ? 'on' : ''} onClick={() => setOverlay('overhangs')}>Overhangs</button>
                <button className={overlay === 'issues' ? 'on' : ''} onClick={() => setOverlay('issues')}>Issues</button>
                <button className={overlay === 'none' ? 'on' : ''} onClick={() => setOverlay('none')}>Plain</button>
              </div>
            </div>
            {overlay === 'overhangs' && (
              <div className="dc-viewer-legend">
                <span><i className="dc-legend-dot" style={{ background: 'var(--rf-primary)' }} />Self-supporting</span>
                <span><i className="dc-legend-dot" style={{ background: 'var(--rf-warning)' }} />Will sag ({report.machine.thresholds.overhangWarnDeg}°+)</span>
                <span><i className="dc-legend-dot" style={{ background: 'var(--rf-danger)' }} />Will fail ({report.machine.thresholds.overhangFailDeg}°+)</span>
              </div>
            )}
          </div>

          <div className="dc-orient-strip">
            {report.orientations.map((o) => {
              const counts = report.summary.statusByOrientation[o.id] || {};
              return (
                <div
                  key={o.id}
                  className={`dc-orient-card ${o.id === orientation.id ? 'selected' : ''}`}
                  onClick={() => setSelectedOrientation(o.id)}
                >
                  {o.id === report.recommended.orientationId && <span className="dc-orient-star">Best</span>}
                  <div className="dc-orient-id">{o.id}</div>
                  <div className="dc-orient-score">{o.score}</div>
                  <div className="dc-orient-label">{o.label}</div>
                  <div className="dc-orient-dots">
                    {[...Array(counts.pass || 0)].map((_, i) => <i key={`p${i}`} style={{ background: 'var(--rf-success)' }} />)}
                    {[...Array(counts.warn || 0)].map((_, i) => <i key={`w${i}`} style={{ background: 'var(--rf-warning)' }} />)}
                    {[...Array(counts.fail || 0)].map((_, i) => <i key={`f${i}`} style={{ background: 'var(--rf-danger)' }} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="dc-card">
          <div className="dc-tabs">
            <button className={tab === 'report' ? 'on' : ''} onClick={() => setTab('report')}>DFM report</button>
            <button className={tab === 'advice' ? 'on' : ''} onClick={() => setTab('advice')}>Bob's advice</button>
            <button className={tab === 'chat' ? 'on' : ''} onClick={() => setTab('chat')}>Ask Bob</button>
          </div>

          {tab === 'report' && (
            <ChecksPanel report={report} orientation={orientation} guideMap={guideMap} />
          )}

          {tab === 'advice' && (
            <div className="dc-advice dc-chat">
              {advice ? (
                <div className="dc-chat-scroll">
                  <div className="chatbot-message assistant" style={{ alignItems: 'stretch' }}>
                    <div className="chatbot-message-content" style={{ maxWidth: '100%' }}>
                      <ChatMessage message={{ role: 'assistant', content: advice }} />
                    </div>
                  </div>
                </div>
              ) : adviceStalled ? (
                <div className="dc-chat-empty">
                  {adviceError || "Bob couldn't finish the written report just now."}
                  <br /><br />
                  <button className="dc-btn primary" onClick={retryAdvice}>Try again</button>
                </div>
              ) : (
                <div className="dc-chat-empty">
                  <div className="dc-spinner" style={{ width: 28, height: 28, marginBottom: 14 }} />
                  Bob is reading your DFM report and writing tailored advice…
                  <br />This usually takes under a minute.
                </div>
              )}
            </div>
          )}

          {tab === 'chat' && <AdvisorChat jobId={jobId} />}
        </div>
      </div>
    </DesignCheckLayout>
  );
}

export default AnalysisPage;
